import React, { useMemo } from "react";

import { Grid, Column } from "../../../../components/src";
import { FONAV_CDS_INDICES } from "./constants";
import { FoNavInstrumentName } from "./FoNavInstrumentName";
import { useFoNavCdsSeries } from "./useFoNavCdsSeries";
import { FoNavGridRow, toBpChangeGridValue, toBpGridValue } from "./utils";

interface FoNavCdsProps {
    startDate: string;
    endDate: string;
}

export function FoNavCds({ startDate, endDate }: FoNavCdsProps): React.ReactElement {
    const { fetching, error, rows } = useFoNavCdsSeries(FONAV_CDS_INDICES, startDate, endDate);

    const tableRows = useMemo((): FoNavGridRow[] => {
        return rows.map((row) => {
            const startRaw = row.startPrice as number | null;
            const endRaw = row.endPrice as number | null;
            return {
                name: row.seriesName ?? row.name,
                instrumentId: row.instrumentId,
                startValue: toBpGridValue(startRaw),
                endValue: toBpGridValue(endRaw),
                change: toBpChangeGridValue(startRaw, endRaw)
            };
        });
    }, [rows]);

    const pendingRows = useMemo(() => rows.filter((row) => row.pendingSeriesName), [rows]);

    if (fetching) {
        return <p>Loading CDS...</p>;
    }

    if (error) {
        return <p>error: {JSON.stringify(error, null, 2)}</p>;
    }

    return (
        <>
            <Grid header="CDS" data={tableRows} className="table-timeseries" tableClassName="table-xs" sortable>
                <Column
                    field="name"
                    className="nowrap left"
                    format={(value: string, item: FoNavGridRow) => <FoNavInstrumentName name={value} instrumentId={item?.instrumentId} />}
                />
                <Column field="startValue" title="Start value (bp)" className="nowrap left" />
                <Column field="endValue" title="End value (bp)" />
                <Column field="change" title="Change (bp)" />
            </Grid>
            {pendingRows.map((row) => (
                <p key={row.name} className="small">
                    {row.pendingSeriesName} is only priced on the end date, {row.seriesName} is used for the period.
                </p>
            ))}
        </>
    );
}
